import { StrictMode, Suspense, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import 'react-phone-input-2/lib/style.css';
import { Provider, useDispatch } from 'react-redux';
import { BrowserRouter, Redirect, Route, Switch } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';

import { ConfigProvider } from 'antd';

import { getToken } from 'firebase/messaging';
import { io } from 'socket.io-client';

import { getMyPlan } from './redux/actions/subscription';
import { setBaseURL } from './redux/actions/user';
import store from './redux/store';

import ErrorBoundary from './components/ErrorBoundary';
import LoadingBar from './components/LoadingBar';

import './assets/scss/style.css';
import { SocketContext } from './context/socket';
import { messaging } from './firebase/config';
import indexRoutes from './routes/index.jsx';
import { UserService } from './services';
import { theme } from './theme/themeConfig';

const PrivateRoute = ({ component: Component, ...rest }) => {
  const dispatch = useDispatch();

  const [socket, setSocket] = useState(null);
  const logoutTimer = useRef();

  const logout = socket => {
    window.localStorage.removeItem('token');
    window.location = '/';

    if (socket) {
      socket.disconnect();
    }
  };

  const onMouseMove = useCallback(() => {
    if (logoutTimer.current) {
      clearInterval(logoutTimer.current);
      logoutTimer.current = setInterval(() => {
        logout();
      }, 960000);
    }
  }, [logoutTimer]);

  useEffect(() => {
    logoutTimer.current = setInterval(() => {
      logout();
    }, 960000);

    return () => {
      clearInterval(logoutTimer.current);
    };
  }, []);

  useLayoutEffect(() => {
    window.addEventListener('mousemove', onMouseMove);

    return () => {
      window.removeEventListener('mousemove', onMouseMove);
    };
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const [protocol, url] = process.env.REACT_APP_BASE_URL.split('://');
    const wsProtocol = protocol === 'http' ? 'ws' : 'wss';

    if (token) {
      setSocket(
        io(`${wsProtocol}://${url}`, {
          transports: ['websocket'],
          auth: {
            token
          }
        })
      );

      dispatch(getMyPlan());
    }
  }, []);

  const requestPermission = async () => {
    const permission = await Notification.requestPermission();

    if (permission === 'granted') {
      const token = await getToken(messaging, {
        vapidKey: process.env.REACT_APP_VAPID_KEY
      });

      await UserService.updateUserSessionPushToken(token);
    }
  };

  useEffect(() => {
    requestPermission();
  }, []);

  return (
    <Route
      {...rest}
      render={props => {
        const token = localStorage.getItem('token');

        if (!token && props.location.pathname) {
          let baseURL = props.location.pathname;
          if (props.location.search) baseURL += '?' + props.location.search;
          if (props.location.hash) baseURL += '#' + props.location.hash;
          dispatch(setBaseURL(baseURL));
        }

        return token ? (
          <SocketContext.Provider value={{ socket }}>
            <Component {...props} />
          </SocketContext.Provider>
        ) : (
          <Redirect to="/auth/login" />
        );
      }}
    />
  );
};

const App = () => (
  <ConfigProvider theme={theme}>
    <ToastContainer />
    <BrowserRouter>
      <Suspense fallback={<LoadingBar fullscreen={true} />}>
        <ErrorBoundary>
          <Provider store={store}>
            <Switch>
              {indexRoutes.map((prop, key) =>
                prop.protected ? (
                  <PrivateRoute {...prop} path={prop.path} key={key} name={prop.name} component={prop.component} />
                ) : (
                  <Route path={prop.path} key={key} component={prop.component} />
                )
              )}
            </Switch>
          </Provider>
        </ErrorBoundary>
      </Suspense>
    </BrowserRouter>
  </ConfigProvider>
);

const container = document.getElementById('root');
const root = createRoot(container);

root.render(
  <StrictMode>
    <App tab="home" />
  </StrictMode>
);
